import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, ArrowUturnLeftIcon } from '@heroicons/react/20/solid';
import { useAuth } from '../contexts/AuthContext';

/* --------------------------------------------------------------------------
   ActAsBanner — shown on every page while a super admin acts as a member.
   Sits in the sticky top block of AppLayout, above the header, so it never
   covers the page or the mobile menu button.
   -------------------------------------------------------------------------- */
export default function ActAsBanner() {
  const { user, actAs, exitActAs, logout } = useAuth();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState('');

  if (!actAs) return null;

  const memberName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || (user?.email ?? '');
  const groupName = actAs.group_name ?? user?.group_name ?? '';
  const fullText = `Vous agissez en tant que ${memberName}${groupName ? ` · ${groupName}` : ''}`;

  const handleExit = async () => {
    setLeaving(true);
    setError('');
    try {
      await exitActAs();
      navigate('/admin/membres');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        logout();
        navigate('/login');
        return;
      }
      setError(err?.response?.data?.message ?? 'Le retour à votre compte a échoué. Réessayez.');
    } finally {
      setLeaving(false);
    }
  };

  return (
    <div className="bg-amber-300 text-amber-950 dark:bg-amber-400">
      <div className="flex items-center gap-x-3 px-4 py-2 sm:px-6 lg:px-8">
        <span className="hidden size-7 shrink-0 items-center justify-center rounded-full bg-amber-950/10 sm:flex">
          <EyeIcon className="size-4" aria-hidden="true" />
        </span>

        <div className="min-w-0 flex-1">
          {/* One line from sm up, two lines below; either way the name line ends
              in an ellipsis when it is too long, and the title holds the full text. */}
          <p className="text-sm/5 sm:truncate" title={fullText} data-testid="act-as-banner-text">
            <span className="block text-xs/4 sm:inline sm:text-sm/5">Vous agissez en tant que </span>
            <span className="block truncate sm:inline">
              <strong className="font-semibold">{memberName}</strong>
              {groupName && <> · {groupName}</>}
            </span>
          </p>
          {error && (
            <p role="alert" className="mt-0.5 text-xs/4 font-medium text-red-800">
              {error}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={handleExit}
          disabled={leaving}
          className="inline-flex shrink-0 items-center gap-x-1.5 whitespace-nowrap rounded-md bg-amber-950 px-3 py-2 text-xs/4 font-semibold text-amber-50 hover:bg-amber-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-950 disabled:cursor-wait disabled:opacity-70 sm:text-sm/5"
        >
          {leaving ? (
            <svg className="size-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <ArrowUturnLeftIcon className="hidden size-4 sm:block" aria-hidden="true" />
          )}
          Revenir à mon compte
        </button>
      </div>
      {/* Hatched edge: marks the borrowed view off from the member's own header */}
      <div
        aria-hidden="true"
        className="h-1 bg-[repeating-linear-gradient(-45deg,currentColor_0_6px,transparent_6px_12px)] opacity-25"
      />
    </div>
  );
}
